import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

export default function CostCalculator() {
  const [scrolled, setScrolled] = useState(false);
  const [usCity, setUsCity] = useState('new-york');
  const [irelandCity, setIrelandCity] = useState('dublin');
  const [currentSalary, setCurrentSalary] = useState(75000);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Comprehensive US cities with accurate cost multipliers (relative to New York = 1.0)
  const usCities = [
    { value: 'new-york', label: 'New York, NY', multiplier: 1.0 },
    { value: 'san-francisco', label: 'San Francisco, CA', multiplier: 0.98 },
    { value: 'los-angeles', label: 'Los Angeles, CA', multiplier: 0.85 },
    { value: 'san-diego', label: 'San Diego, CA', multiplier: 0.82 },
    { value: 'san-jose', label: 'San Jose, CA', multiplier: 0.95 },
    { value: 'boston', label: 'Boston, MA', multiplier: 0.88 },
    { value: 'seattle', label: 'Seattle, WA', multiplier: 0.84 },
    { value: 'washington-dc', label: 'Washington, DC', multiplier: 0.86 },
    { value: 'chicago', label: 'Chicago, IL', multiplier: 0.75 },
    { value: 'miami', label: 'Miami, FL', multiplier: 0.78 },
    { value: 'atlanta', label: 'Atlanta, GA', multiplier: 0.68 },
    { value: 'philadelphia', label: 'Philadelphia, PA', multiplier: 0.72 },
    { value: 'portland', label: 'Portland, OR', multiplier: 0.76 },
    { value: 'denver', label: 'Denver, CO', multiplier: 0.74 },
    { value: 'austin', label: 'Austin, TX', multiplier: 0.72 },
    { value: 'dallas', label: 'Dallas, TX', multiplier: 0.68 },
    { value: 'houston', label: 'Houston, TX', multiplier: 0.65 },
    { value: 'phoenix', label: 'Phoenix, AZ', multiplier: 0.64 },
    { value: 'minneapolis', label: 'Minneapolis, MN', multiplier: 0.70 },
    { value: 'detroit', label: 'Detroit, MI', multiplier: 0.58 },
    { value: 'nashville', label: 'Nashville, TN', multiplier: 0.69 },
    { value: 'charlotte', label: 'Charlotte, NC', multiplier: 0.66 },
    { value: 'raleigh', label: 'Raleigh, NC', multiplier: 0.67 },
    { value: 'tampa', label: 'Tampa, FL', multiplier: 0.70 },
    { value: 'orlando', label: 'Orlando, FL', multiplier: 0.69 },
    { value: 'las-vegas', label: 'Las Vegas, NV', multiplier: 0.67 },
    { value: 'salt-lake-city', label: 'Salt Lake City, UT', multiplier: 0.68 },
    { value: 'pittsburgh', label: 'Pittsburgh, PA', multiplier: 0.62 },
    { value: 'baltimore', label: 'Baltimore, MD', multiplier: 0.71 },
    { value: 'columbus', label: 'Columbus, OH', multiplier: 0.63 },
    { value: 'indianapolis', label: 'Indianapolis, IN', multiplier: 0.60 },
    { value: 'milwaukee', label: 'Milwaukee, WI', multiplier: 0.64 },
    { value: 'kansas-city', label: 'Kansas City, MO', multiplier: 0.61 },
    { value: 'st-louis', label: 'St. Louis, MO', multiplier: 0.59 },
    { value: 'cincinnati', label: 'Cincinnati, OH', multiplier: 0.61 },
    { value: 'cleveland', label: 'Cleveland, OH', multiplier: 0.60 },
    { value: 'sacramento', label: 'Sacramento, CA', multiplier: 0.76 },
    { value: 'albuquerque', label: 'Albuquerque, NM', multiplier: 0.58 },
    { value: 'tucson', label: 'Tucson, AZ', multiplier: 0.60 },
    { value: 'oklahoma-city', label: 'Oklahoma City, OK', multiplier: 0.56 },
    { value: 'memphis', label: 'Memphis, TN', multiplier: 0.57 },
    { value: 'louisville', label: 'Louisville, KY', multiplier: 0.59 },
    { value: 'richmond', label: 'Richmond, VA', multiplier: 0.65 },
    { value: 'buffalo', label: 'Buffalo, NY', multiplier: 0.61 },
    { value: 'rochester', label: 'Rochester, NY', multiplier: 0.62 }
  ];

  // All 26 counties of Republic of Ireland with accurate cost data
  const irelandCities = [
    { value: 'dublin', label: 'Dublin', rentMultiplier: 1.0, costMultiplier: 1.0 },
    { value: 'cork', label: 'Cork', rentMultiplier: 0.68, costMultiplier: 0.86 },
    { value: 'galway', label: 'Galway', rentMultiplier: 0.72, costMultiplier: 0.88 },
    { value: 'limerick', label: 'Limerick', rentMultiplier: 0.62, costMultiplier: 0.83 },
    { value: 'waterford', label: 'Waterford', rentMultiplier: 0.58, costMultiplier: 0.80 },
    { value: 'kilkenny', label: 'Kilkenny', rentMultiplier: 0.55, costMultiplier: 0.78 },
    { value: 'wicklow', label: 'Wicklow', rentMultiplier: 0.70, costMultiplier: 0.87 },
    { value: 'meath', label: 'Meath', rentMultiplier: 0.66, costMultiplier: 0.85 },
    { value: 'kildare', label: 'Kildare', rentMultiplier: 0.68, costMultiplier: 0.86 },
    { value: 'clare', label: 'Clare', rentMultiplier: 0.53, costMultiplier: 0.77 },
    { value: 'mayo', label: 'Mayo', rentMultiplier: 0.48, costMultiplier: 0.74 },
    { value: 'donegal', label: 'Donegal', rentMultiplier: 0.45, costMultiplier: 0.72 },
    { value: 'louth', label: 'Louth', rentMultiplier: 0.60, costMultiplier: 0.82 },
    { value: 'westmeath', label: 'Westmeath', rentMultiplier: 0.53, costMultiplier: 0.78 },
    { value: 'offaly', label: 'Offaly', rentMultiplier: 0.50, costMultiplier: 0.76 },
    { value: 'laois', label: 'Laois', rentMultiplier: 0.53, costMultiplier: 0.78 },
    { value: 'carlow', label: 'Carlow', rentMultiplier: 0.55, costMultiplier: 0.79 },
    { value: 'wexford', label: 'Wexford', rentMultiplier: 0.58, costMultiplier: 0.81 },
    { value: 'tipperary', label: 'Tipperary', rentMultiplier: 0.50, costMultiplier: 0.76 },
    { value: 'kerry', label: 'Kerry', rentMultiplier: 0.58, costMultiplier: 0.81 },
    { value: 'roscommon', label: 'Roscommon', rentMultiplier: 0.45, costMultiplier: 0.73 },
    { value: 'sligo', label: 'Sligo', rentMultiplier: 0.50, costMultiplier: 0.76 },
    { value: 'leitrim', label: 'Leitrim', rentMultiplier: 0.42, costMultiplier: 0.71 },
    { value: 'cavan', label: 'Cavan', rentMultiplier: 0.48, costMultiplier: 0.75 },
    { value: 'monaghan', label: 'Monaghan', rentMultiplier: 0.48, costMultiplier: 0.75 },
    { value: 'longford', label: 'Longford', rentMultiplier: 0.45, costMultiplier: 0.73 }
  ];

  const selectedUsCity = usCities.find(c => c.value === usCity)!;
  const selectedIrelandCity = irelandCities.find(c => c.value === irelandCity)!;

  // Base costs in Dublin (monthly EUR)
  const dublinBaseCosts = {
    rent1bed: 1850,
    rent3bed: 2900,
    utilities: 180,
    groceries: 400,
    transport: 135,
    dining: 300,
    entertainment: 200,
    healthcare: 150
  };

  // Calculate Ireland costs
  const irelandCosts = {
    rent1bed: Math.round(dublinBaseCosts.rent1bed * selectedIrelandCity.rentMultiplier),
    rent3bed: Math.round(dublinBaseCosts.rent3bed * selectedIrelandCity.rentMultiplier),
    utilities: Math.round(dublinBaseCosts.utilities * selectedIrelandCity.costMultiplier),
    groceries: Math.round(dublinBaseCosts.groceries * selectedIrelandCity.costMultiplier),
    transport: Math.round(dublinBaseCosts.transport * selectedIrelandCity.costMultiplier),
    dining: Math.round(dublinBaseCosts.dining * selectedIrelandCity.costMultiplier),
    entertainment: Math.round(dublinBaseCosts.entertainment * selectedIrelandCity.costMultiplier),
    healthcare: Math.round(dublinBaseCosts.healthcare * selectedIrelandCity.costMultiplier)
  };

  // Calculate US costs (converted to EUR for comparison)
  const exchangeRate = 0.92; // USD to EUR
  const usCosts = {
    rent1bed: Math.round(2500 * selectedUsCity.multiplier * exchangeRate),
    rent3bed: Math.round(4200 * selectedUsCity.multiplier * exchangeRate),
    utilities: Math.round(150 * selectedUsCity.multiplier * exchangeRate),
    groceries: Math.round(450 * selectedUsCity.multiplier * exchangeRate),
    transport: Math.round(200 * selectedUsCity.multiplier * exchangeRate),
    dining: Math.round(350 * selectedUsCity.multiplier * exchangeRate),
    entertainment: Math.round(250 * selectedUsCity.multiplier * exchangeRate),
    healthcare: Math.round(400 * selectedUsCity.multiplier * exchangeRate)
  };

  const totalIreland = Object.values(irelandCosts).reduce((a, b) => a + b, 0);
  const totalUS = Object.values(usCosts).reduce((a, b) => a + b, 0);
  const difference = totalUS - totalIreland;
  const percentDiff = Math.round((difference / totalUS) * 100);

  // Salary calculation
  const irelandTaxRate = currentSalary > 70000 ? 0.48 : 0.40;
  const usTaxRate = 0.30;
  const irelandNetMonthly = Math.round((currentSalary * (1 - irelandTaxRate)) / 12);
  const usNetMonthly = Math.round((currentSalary * exchangeRate * (1 - usTaxRate)) / 12);

  const categories = [
    { key: 'rent1bed', label: '1-Bedroom Apartment (City Center)', icon: 'ri-home-4-line' },
    { key: 'rent3bed', label: '3-Bedroom Apartment (City Center)', icon: 'ri-building-line' },
    { key: 'utilities', label: 'Utilities (Electric, Water, Internet)', icon: 'ri-flashlight-line' },
    { key: 'groceries', label: 'Groceries (Monthly)', icon: 'ri-shopping-cart-line' },
    { key: 'transport', label: 'Public Transportation', icon: 'ri-bus-line' },
    { key: 'dining', label: 'Dining Out', icon: 'ri-restaurant-line' },
    { key: 'entertainment', label: 'Entertainment & Leisure', icon: 'ri-movie-line' },
    { key: 'healthcare', label: 'Healthcare (Insurance)', icon: 'ri-heart-pulse-line' }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar scrolled={scrolled} />
      
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-[#0A381F] mb-4">Cost of Living Calculator</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Compare your current expenses across 45 major US cities with all 26 counties in Ireland to plan your budget accurately
            </p>
          </div>

          {/* Input Section */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mb-12">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-[#0A381F] mb-3">Your Current US City</label>
                <select
                  value={usCity}
                  onChange={(e) => setUsCity(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] cursor-pointer text-base"
                >
                  {usCities.map(city => (
                    <option key={city.value} value={city.value}>{city.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0A381F] mb-3">Irish County to Compare</label>
                <select
                  value={irelandCity}
                  onChange={(e) => setIrelandCity(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] cursor-pointer text-base"
                >
                  {irelandCities.map(city => (
                    <option key={city.value} value={city.value}>{city.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#0A381F] mb-3">Annual Salary (USD)</label>
                <input
                  type="number"
                  value={currentSalary}
                  onChange={(e) => setCurrentSalary(Number(e.target.value))}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-base"
                  min="0"
                  step="1000"
                />
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 text-red-600 rounded-xl">
                  <i className="ri-map-pin-line text-2xl"></i>
                </div>
                <h3 className="font-bold text-[#0A381F]">{selectedUsCity.label}</h3>
              </div>
              <p className="text-3xl font-bold text-[#0A381F] mb-2">€{totalUS.toLocaleString()}</p>
              <p className="text-sm text-gray-600">Monthly Cost of Living</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 text-green-600 rounded-xl">
                  <i className="ri-map-pin-2-line text-2xl"></i>
                </div>
                <h3 className="font-bold text-[#0A381F]">{selectedIrelandCity.label}</h3>
              </div>
              <p className="text-3xl font-bold text-[#0A381F] mb-2">€{totalIreland.toLocaleString()}</p>
              <p className="text-sm text-gray-600">Monthly Cost of Living</p>
            </div>

            <div className={`rounded-2xl p-6 shadow-lg border ${difference > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 flex items-center justify-center ${difference > 0 ? 'bg-green-600' : 'bg-red-600'} text-white rounded-xl`}>
                  <i className={`${difference > 0 ? 'ri-arrow-down-line' : 'ri-arrow-up-line'} text-2xl`}></i>
                </div>
                <h3 className="font-bold text-[#0A381F]">Difference</h3>
              </div>
              <p className={`text-3xl font-bold mb-2 ${difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {difference > 0 ? '-' : '+'}€{Math.abs(difference).toLocaleString()}
              </p>
              <p className="text-sm text-gray-600">
                {difference > 0 ? `${percentDiff}% cheaper in Ireland` : `${Math.abs(percentDiff)}% more expensive in Ireland`}
              </p>
            </div>
          </div>

          {/* Detailed Comparison */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mb-12">
            <h2 className="text-2xl font-bold text-[#0A381F] mb-6">Detailed Cost Breakdown</h2>
            <div className="space-y-4">
              {categories.map((category) => {
                const usValue = usCosts[category.key as keyof typeof usCosts];
                const ieValue = irelandCosts[category.key as keyof typeof irelandCosts];
                const diff = usValue - ieValue;
                const maxValue = Math.max(usValue, ieValue);

                return (
                  <div key={category.key} className="border-b border-gray-100 pb-4 last:border-0">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 flex items-center justify-center text-[#0A381F]">
                        <i className={`${category.icon} text-xl`}></i>
                      </div>
                      <h4 className="font-semibold text-[#0A381F] flex-1">{category.label}</h4>
                      <span className={`text-sm font-semibold ${diff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {diff > 0 ? '-' : '+'}€{Math.abs(diff)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">{selectedUsCity.label}</span>
                          <span className="text-sm font-bold text-[#0A381F]">€{usValue}</span>
                        </div>
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-red-400"
                            style={{ width: `${(usValue / maxValue) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">{selectedIrelandCity.label}</span>
                          <span className="text-sm font-bold text-[#0A381F]">€{ieValue}</span>
                        </div>
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-400"
                            style={{ width: `${(ieValue / maxValue) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Salary Comparison */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mb-12">
            <h2 className="text-2xl font-bold text-[#0A381F] mb-6">Net Income After Tax</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h4 className="font-bold text-[#0A381F] mb-4">{selectedUsCity.label}</h4>
                <p className="text-3xl font-bold text-[#0A381F] mb-2">€{usNetMonthly.toLocaleString()}</p>
                <p className="text-sm text-gray-600 mb-4">Monthly Net Income</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">After living costs:</span>
                    <span className="font-bold text-[#0A381F]">€{(usNetMonthly - totalUS).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h4 className="font-bold text-[#0A381F] mb-4">{selectedIrelandCity.label}</h4>
                <p className="text-3xl font-bold text-[#0A381F] mb-2">€{irelandNetMonthly.toLocaleString()}</p>
                <p className="text-sm text-gray-600 mb-4">Monthly Net Income</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">After living costs:</span>
                    <span className="font-bold text-[#0A381F]">€{(irelandNetMonthly - totalIreland).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-[#0A381F] text-white rounded-2xl p-8 text-center mb-12">
            <h3 className="text-2xl font-bold mb-4">Ready to Make Your Move?</h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Connect with verified financial advisors, tax consultants, and relocation experts who can help you plan your budget and navigate the financial aspects of moving to Ireland.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/directory?category=Finance"
                className="inline-block bg-[#D4AF37] hover:bg-[#C19B2F] text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Find Financial Experts
              </Link>
              <a
                href="https://wise.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-block bg-white text-[#0A381F] px-8 py-4 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap hover:bg-gray-100"
              >
                <i className="ri-exchange-dollar-line mr-2"></i>
                Open Wise Account
              </a>
            </div>
          </div>

          {/* Recommended Services */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Recommended Financial Services</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <a
                href="https://wise.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="p-6 bg-gradient-to-br from-[#0A381F]/5 to-[#D4AF37]/5 rounded-xl hover:shadow-md transition-all cursor-pointer border border-gray-100"
              >
                <div className="w-12 h-12 bg-[#0A381F] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-exchange-dollar-line text-white text-2xl"></i>
                </div>
                <h4 className="font-bold text-[#0A381F] mb-2">Wise Money Transfer</h4>
                <p className="text-sm text-gray-600 mb-3">Save up to 8x on international transfers with real exchange rates</p>
                <span className="text-sm font-semibold text-[#D4AF37]">Get €75 Bonus →</span>
              </a>

              <a
                href="https://revolut.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="p-6 bg-gradient-to-br from-[#0A381F]/5 to-[#D4AF37]/5 rounded-xl hover:shadow-md transition-all cursor-pointer border border-gray-100"
              >
                <div className="w-12 h-12 bg-[#0A381F] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-bank-card-line text-white text-2xl"></i>
                </div>
                <h4 className="font-bold text-[#0A381F] mb-2">Revolut Banking</h4>
                <p className="text-sm text-gray-600 mb-3">Digital banking designed for expats and international living</p>
                <span className="text-sm font-semibold text-[#D4AF37]">Open Free Account →</span>
              </a>

              <a
                href="https://www.greenbacktaxservices.com"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="p-6 bg-gradient-to-br from-[#0A381F]/5 to-[#D4AF37]/5 rounded-xl hover:shadow-md transition-all cursor-pointer border border-gray-100"
              >
                <div className="w-12 h-12 bg-[#0A381F] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-file-text-line text-white text-2xl"></i>
                </div>
                <h4 className="font-bold text-[#0A381F] mb-2">Greenback Tax Services</h4>
                <p className="text-sm text-gray-600 mb-3">US expat tax specialists for Americans living abroad</p>
                <span className="text-sm font-semibold text-[#D4AF37]">Free Consultation →</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
